import { client } from '@studio/api/api-client/client';
import { CENTRAL_API_V2 } from '@studio/constants/env-variables';
import { alertError } from '@studio/helpers/alertError';
import OpaPermissions from '@studio/constants/opa-permissions';
import Permissions from '@studio/constants/permissions';
import { authenticationOpaPermissionsAtom } from '@studio/storeV2/authentication/authentication';
import * as SessionStore from '@studio/storeV2/authentication/session-store';
import * as Store from '@studio/storeV2/store';
import type { ApiClientConfig } from '@studio/types/axios';

const _ME_PERMISSIONS_URL = `${CENTRAL_API_V2}/users/me/permissions`;

/**
 * GET `/users/me/permissions`, write the OPA permissions atom directly (no bridge). For Resi
 * admins, cross-checks the response against the `OpaPermissions` class and alerts on mismatch.
 * Must run AFTER `reset()` on login so it writes the fresh provider store (#3591).
 */
export default async function fetchMePermissions(): Promise<void> {
  const user = SessionStore.session.getUser();
  const isResiAdmin = Boolean(user?.capabilities.includes(Permissions.SUDO));
  const config: ApiClientConfig = { token: SessionStore.session.getAccessToken() };

  Store.store.set(authenticationOpaPermissionsAtom, (prev) => ({ ...prev, error: null, isFetching: true }));
  try {
    const response: string[] = await client(_ME_PERMISSIONS_URL, config);

    if (isResiAdmin) {
      const apiPermissions = new Set(response);
      const opaPermissions = Object.values(OpaPermissions);
      const permsMissingFromClass = [...apiPermissions].filter((permission) => !opaPermissions.includes(permission));
      const permsMissingFromApi = opaPermissions.filter((permission) => !apiPermissions.has(permission));

      if (permsMissingFromClass.length > 0 || permsMissingFromApi.length > 0) {
        const mismatchMessages: string[] = [];
        if (permsMissingFromClass.length > 0) {
          mismatchMessages.push(
            `In API response and not in OPA permissions class: ${permsMissingFromClass.join(', ')}`
          );
        }
        if (permsMissingFromApi.length > 0) {
          mismatchMessages.push(`Not in API response and in OPA permissions class: ${permsMissingFromApi.join(', ')}`);
        }
        alertError(mismatchMessages.join(' | '));
      }
    }

    Store.store.set(authenticationOpaPermissionsAtom, { data: response, error: null, isFetching: false });
  } catch (error) {
    if (error instanceof Error) {
      Store.store.set(authenticationOpaPermissionsAtom, (prev) => ({ ...prev, error, isFetching: false }));
    }
  }
}
